import React, { useState } from 'react';
import { useAnimationControls } from 'framer-motion';
import { useUserUpdateContext } from 'src/lib/contexts/user-update-context';
import { useRouter, useUserInfo } from 'src/hooks';
import { ProductDetailsResponse } from 'src/services/productServices';
import { useActivateOfferMutation } from 'src/services/easyRewardsServices';
import {
    ActivateButton,
    isActivationSuccess,
} from 'src/screens/EasyRewards/OfferList/OfferCard';
import { setPreviousPath } from 'src/lib/utils/setPreviousPath';
import ROUTES from 'src/lib/routes';
import { useProductBrowseContext } from 'src/lib/contexts/product-browse-context';
import { Body, Button, Heading } from '../UI';
import ProductStarRating from '../product-star-rating';
import { IconClose, IconSmallCheckmark, IconWarning } from '../Icons';
import styles from './styles.module.scss';

interface ProductCardProps {
    productDetails: ProductDetailsResponse;
    productCode: string;
    onClose: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

function ProductCard({
    productDetails,
    productCode,
    onClose,
}: ProductCardProps) {
    const [lottieRef, setLottieRef] = useState<any>(null);
    const [buttonContent, setButtonContent] = useState(<span>Activate</span>);
    const { setGuestModal } = useUserUpdateContext();
    const { userInfo } = useUserInfo();
    const { mutateAsync: handleOfferActivate } = useActivateOfferMutation();
    const activateButtonAnimationControls = useAnimationControls();
    const { push, location } = useRouter();
    const { fromPath } = useProductBrowseContext();

    const handleSignIn = () => {
        setGuestModal({ open: true });
    };

    const activateOffer = (offerId: string) => {
        handleOfferActivate([offerId])
            .then((res) => {
                if (
                    !(
                        res?.data?.issuccess &&
                        isActivationSuccess([offerId], res.data.offers)
                    )
                ) {
                    setButtonContent(
                        <span
                            className={styles.activatedButtonContent}
                            aria-label="Your offer is activated"
                        >
                            <IconSmallCheckmark /> Activated
                        </span>
                    );
                    activateButtonAnimationControls.set({
                        backgroundColor: '#ffffff',
                        color: '#5b0069',
                    });
                }
                console.log('Offer activated successfully', res);
            })
            .catch(() => {
                console.log('Error activating offer');
            });
    };

    const handleActivate = () => {
        if (productDetails.easyRewardsOffer?.offerId) {
            if (lottieRef) {
                lottieRef.play();
            }
            activateOffer(
                productDetails?.easyRewardsOffer.offerId as unknown as string
            );
        }
    };

    return (
        <button
            type="button"
            onClick={() =>
                push(
                    `${ROUTES.productDetailsPage}?barcode=${productCode}&isBarCodeScan=true`,
                    setPreviousPath(location, fromPath)
                )
            }
            className={styles.productCard}
        >
            <div className={styles.productContainer}>
                <img
                    aria-hidden="true"
                    className={styles.productImage}
                    src={productDetails.imageURL}
                    alt="Product"
                />
                <div className={styles.productInfoContainer}>
                    <Heading type="h3" id="product-name">
                        {productDetails.name}
                    </Heading>
                    <Body
                        weight="light"
                        id="product-description"
                        color="textMediumGray"
                        size={12}
                    >
                        {productDetails.description}
                    </Body>
                    <ProductStarRating ratingValue={productDetails.rating} />
                    <div className={styles.dollarAmounts}>
                        <Heading type="h3" size={20} id="product-price">
                            ${productDetails.finalPrice}
                        </Heading>
                        {productDetails.finalPrice !==
                            productDetails.basePrice && (
                            <Body
                                weight="light"
                                size={12}
                                className={styles.strikeThrough}
                                color="textMediumGray"
                                id="product-original-price"
                            >
                                ${productDetails.basePrice}
                            </Body>
                        )}
                    </div>
                </div>
                <Button
                    variant="icon"
                    className={styles.closeButton}
                    id="close-button"
                    onClick={onClose}
                >
                    <IconClose height={13} width={13} fillColor="fillBlack" />
                </Button>
            </div>
            {productDetails.easyRewardsOffer && (
                <div className={styles.rewardsContainer}>
                    <Body
                        display="inline"
                        size={14}
                        weight="light"
                        color="textBlack"
                    >
                        <span className={styles.rewardsHeadline}>
                            {productDetails.easyRewardsOffer.headliner}
                        </span>{' '}
                        <span>{productDetails.easyRewardsOffer.message}</span>
                    </Body>
                    {!userInfo ? (
                        <Button
                            onClick={handleSignIn}
                            id="sign-in-button"
                            className={styles.signInBtn}
                        >
                            Sign in
                        </Button>
                    ) : (
                        <ActivateButton
                            onClick={handleActivate}
                            buttonContent={buttonContent}
                            setLottieRef={setLottieRef}
                            activateButtonAnimationControls={
                                activateButtonAnimationControls
                            }
                        />
                    )}
                </div>
            )}
        </button>
    );
}

interface ProductCardWrapperProps {
    productDetails: ProductDetailsResponse | null | undefined;
    productCode: string | null;
    onClose: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    scannerError?: boolean;
}

function ProductCardWrapper({
    productDetails,
    productCode,
    onClose,
    scannerError,
}: ProductCardWrapperProps) {
    if (scannerError) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorButtonContainer}>
                    <IconWarning />
                    <Button
                        variant="icon"
                        className={styles.closeButton}
                        id="close-button"
                        onClick={onClose}
                    >
                        <IconClose
                            height={13}
                            width={13}
                            fillColor="fillBlack"
                        />
                    </Button>
                </div>
                <Body size={14} weight="light">
                    We apologize, but we were unable to locate the product.
                    Please consult an associate for further assistance.
                </Body>
            </div>
        );
    }
    if (productDetails && productCode) {
        return (
            <ProductCard
                productDetails={productDetails}
                productCode={productCode}
                onClose={onClose}
            />
        );
    }
    return null;
}
export default ProductCardWrapper;
